import {
  Box,
  Button,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Check from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { PRESCRIPTION_BUILDER_VIEW_TYPES_ARRAY } from 'src/consumer/constants'
import { EMRSections } from 'src/mentor/components/MentorSettingsPage/components/EmrSections'

export default function EmrSectionsEditorDialog({
  open,
  handleClose,
  refreshEmrSections,
  mentorProfileData,
  emrSectionsApiSignature,
  emrSettingsSaveApiSignature,
  hideReordering = false,
  /** When set, show only this report type's sections (e.g. 'assessment-report'). Tabs hidden. */
  emrReportType,
  /** Current Editor Layout Type + session-only switch handler (shown inline below). */
  editorLayoutType,
  onChangeEditorLayout,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const sectionComponentRef = useRef()
  const [saveLoading, setSaveLoading] = useState(false)
  
  const handleSave = async () => {
    if (saveLoading) {
      return
    }
    setSaveLoading(true)
    try {
      const saveResult = await sectionComponentRef.current?.save()
      if (saveResult) {
        // pass the saved map through so the page behind the drawer updates instantly
        refreshEmrSections(saveResult)
        handleClose()
      } else {
        // keep the drawer open so the user's changes are not silently lost
        toast.error('Could not save EMR settings — please try again')
      }
    } finally {
      setSaveLoading(false)
    }
  }
  
  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '600px',
          maxWidth: isMobile ? '100%' : '90vw',
          height: isMobile ? '90vh' : '100%',
          maxHeight: isMobile ? '90vh' : '100%',
          borderTopLeftRadius: isMobile ? '16px 16px 0 0' : '0',
          borderBottomLeftRadius: isMobile ? '0' : '0',
        },
      }}
      ModalProps={{
        keepMounted: true, // Keep mounted to allow pre-fetching data
      }}
    >
      {/* Header */}
      <Toolbar
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          minHeight: '72px !important',
          background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
          borderBottom: '1px solid rgba(67, 56, 202, 0.12)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#1e293b',
            fontSize: { xs: '18px', sm: '20px' },
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          EMR Sections Editor
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: '#64748b',
            '&:hover': {
              backgroundColor: 'rgba(67, 56, 202, 0.08)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
      
      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Always render EMRSections so it can start fetching data immediately when drawer opens */}
        {/* The component has its own loading state that shows LinearProgress */}
        {open && (
          <EMRSections
            ref={sectionComponentRef}
            hideSaveButton
            hideReportsTabsRelatedActions
            hideEmrSectionHeader
            hideReordering={hideReordering}
            hidePrintOnlyEditorOnlyToggles
            mentorProfileData={mentorProfileData}
            emrSectionsApiSignature={emrSectionsApiSignature}
            emrSettingsSaveApiSignature={emrSettingsSaveApiSignature}
            hideTabs={Boolean(emrReportType) || undefined}
            initalTab={emrReportType || 'prescription'}
            singleReportTypeId={emrReportType}
          />
        )}

        {/* Editor Layout — shown inline below the sections editor. Session-only:
            switching re-renders the editor body immediately but is never persisted. */}
        {onChangeEditorLayout && (
          <Box
            sx={{
              mt: 3,
              pt: 2.5,
              borderTop: '1px solid rgba(67, 56, 202, 0.12)',
            }}
          >
            <Typography
              sx={{ fontSize: '13px', fontWeight: 700, color: '#475569', mb: 0.25 }}
            >
              Editor Layout
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#94a3b8', mb: 1.25 }}>
              Applies to this session only — not saved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {PRESCRIPTION_BUILDER_VIEW_TYPES_ARRAY.map((viewType) => {
                const selected = viewType.id === editorLayoutType
                return (
                  <Button
                    key={viewType.id}
                    onClick={() => onChangeEditorLayout(viewType.id)}
                    variant={selected ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={selected ? <Check sx={{ fontSize: 16 }} /> : null}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      ...(selected
                        ? {
                            background:
                              'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
                            '&:hover': {
                              background:
                                'linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)',
                            },
                          }
                        : {
                            borderColor: 'rgba(67, 56, 202, 0.3)',
                            color: '#4338ca',
                            '&:hover': {
                              borderColor: '#4338ca',
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                            },
                          }),
                    }}
                  >
                    {viewType.title}
                  </Button>
                )
              })}
            </Box>
          </Box>
        )}
      </Box>
      
      {/* Footer Actions */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          borderTop: '1px solid rgba(67, 56, 202, 0.12)',
          background: '#fafafa',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          disabled={saveLoading}
          variant="outlined"
          sx={{
            textTransform: 'none',
            px: 3,
            borderColor: 'rgba(67, 56, 202, 0.3)',
            color: '#4338ca',
            '&:hover': {
              borderColor: '#4338ca',
              backgroundColor: 'rgba(67, 56, 202, 0.08)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saveLoading}
          sx={{
            textTransform: 'none',
            px: 3,
            background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)',
            },
          }}
        >
          Save {saveLoading && <Spinner animation="border" variant="light" size="sm" style={{ marginLeft: '8px' }} />}
        </Button>
      </Box>
    </Drawer>
  )
}
